import React from 'react';
import styled from 'styled-components';
import { Collapse } from 'react-collapse';
import {
  IconAdd,
  IconCaretDown,
  IconCaretRight,
  IconDelete,
  IconEdit,
  IconMore,
  IconAddPerson,
} from '@bynder/icons';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';
import { Badge, Button, Dropdown, token } from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import {
  Chapter,
  ModalType,
  PageStates,
  Page,
  Themeset,
  Guide,
} from '../../types';
import { useActiveGuide } from '../../queries/guides';
import { AbilityContext } from '../../context/ability';
import {
  GUIDELINES_PERMISSIONS,
  PERMISSION_ENTITIES,
} from '../../helpers/permissions';
import { GuideAbility } from '../../helpers/abilities';
import { ROUTES } from '../../constants/app';
import SidebarTableOfContents from './sidebar/TableOfContents';
import Navigation from '../shared/Navigation';
import { usePageMutations } from '../../mutations/pages';
import { generateUUID } from '../../helpers/utils';
import { AddChapterButton } from './sidebar/AddChapterButton';
import { useChapterMutations } from '../../mutations/widgets/chapters';
import { EditableSidebarItem } from '../shared/EditableSidebarItem';
import { useCurrentUserIdentity } from '../../queries/identity';
import { useModuleTier } from '../../helpers/hooks';
import { CHAPTER_NAME_MAX_LENGTH } from '../../constants/chapters';
import { getAlphaColor } from '../shared/styled-components/Header';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { size } from '../../constants/styles';
import { InspectorSetting } from '../../constants/guides';
import { StyledChapterItemTitle, StyledPageTitle } from './Styles';
import { useGuideStore } from '../../stores/guideStore';
import { useSidebarStore } from '../../stores/sidebarStore';
import { useInspectorStore } from '../../stores/inspectorStore';
import { useModalStore } from '../../stores/modalStore';
import { usePageTemplateModal } from '../../hooks/useModals';
import { EntityEnum } from '../modals/NewCreateModal';

const SidebarView: React.FC<{ guide: Guide }> = ({ guide }) => {
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { addChapter, moveChapter } = useChapterMutations();
  const { reorderSidebarPage } = usePageMutations();
  const onAddChapter = (chapterTitle: string) => {
    const newChapterId = generateUUID();
    addChapter.mutate({ newChapterId, chapterTitle });
  };

  const onDragEnd = ({ source, destination, draggableId }: DropResult) => {
    const isSameDestination =
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index);

    if (isSameDestination) return;

    // Chapter Reordering
    if (source.droppableId === 'chaptersDroppable') {
      const chapterId = draggableId;
      moveChapter.mutate({
        chapterId,
        fromPosition: source.index,
        toPosition: destination.index,
      });
    } else {
      // Page Reordering
      reorderSidebarPage.mutate({
        pageId: draggableId,
        fromChapterId: source.droppableId,
        fromPosition: source.index,
        toChapterId: destination.droppableId,
        toPosition: destination.index,
      });
    }
  };

  if (isEditMode) {
    return (
      <Navigation>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="chaptersDroppable" type="CHAPTER">
            {provided => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {guide?.chapters.map((chapter, index) => (
                  <Draggable
                    key={chapter.id}
                    draggableId={chapter.id}
                    index={index}
                  >
                    {(draggableProvided, snapshot) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        <SidebarChapter
                          guideOwnerId={guide.ownerId}
                          isDragging={snapshot.isDragging}
                          chapter={chapter}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {guide?.canEdit && <AddChapterButton onAddChapter={onAddChapter} />}
      </Navigation>
    );
  }

  return (
    <Navigation>
      {guide?.chapters.map(chapter => (
        <SidebarChapter
          key={chapter.id}
          chapter={chapter}
          guideOwnerId={guide.ownerId}
        />
      ))}
    </Navigation>
  );
};

export default SidebarView;

const SidebarChapter: React.FC<{
  isDragging?: boolean;
  chapter: Chapter;
  guideOwnerId: string;
}> = ({ chapter, isDragging, guideOwnerId }) => {
  const activePageId = useGuideStore(s => s.activePageId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const [isAccordionOpen, setIsAccordionOpen] = React.useState(false);

  const areSidebarItemsCollapsed = useSidebarStore(
    s => s.areSidebarItemsCollapsed,
  );

  React.useEffect(() => {
    if (areSidebarItemsCollapsed) {
      setIsAccordionOpen(false);
    } else {
      setIsAccordionOpen(true);
    }
  }, [areSidebarItemsCollapsed]);

  React.useEffect(() => {
    const chapterIsActive = chapter?.pages?.find(
      ({ id }) => id === activePageId,
    );

    if (chapterIsActive) {
      setIsAccordionOpen(true);
    }
  }, [activePageId, chapter?.pages]);

  if (isEditMode)
    return (
      <StyledChapterItem $isDragging={isDragging}>
        <Droppable droppableId={chapter.id}>
          {(droppableProvided: DroppableProvided, { isDraggingOver }) => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              <SidebarChapterHeader
                chapter={chapter}
                isAccordionOpen={isAccordionOpen}
                setIsAccordionOpen={setIsAccordionOpen}
              />
              <Collapse
                isOpened={
                  chapter?.pages.length === 0 ||
                  isAccordionOpen ||
                  isDraggingOver
                }
              >
                {chapter?.pages.map((page, index) => (
                  <Draggable key={page.id} draggableId={page.id} index={index}>
                    {(draggableProvided, snapshot) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        <SidebarPage
                          guideOwnerId={guideOwnerId}
                          key={page.id}
                          page={page}
                          isDragging={snapshot.isDragging}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
              </Collapse>
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </StyledChapterItem>
    );

  return (
    chapter?.pages.length > 0 && (
      <StyledChapterItem>
        <SidebarChapterHeader
          chapter={chapter}
          isAccordionOpen={isAccordionOpen}
          setIsAccordionOpen={setIsAccordionOpen}
        />
        <Collapse isOpened={isAccordionOpen}>
          {chapter?.pages.map(page => (
            <SidebarPage
              key={page.id}
              page={page}
              isDragging={false}
              guideOwnerId={guideOwnerId}
            />
          ))}
        </Collapse>
      </StyledChapterItem>
    )
  );
};

const SidebarPage: React.FC<{
  page: Page;
  isDragging: boolean;
  guideOwnerId: string;
}> = ({ page, isDragging, guideOwnerId }) => {
  const history = useHistory();
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const setActivePageId = useGuideStore(s => s.setActivePageId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const setIsEditMode = useGuideStore(s => s.setIsEditMode);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );

  const { editPageTitle } = usePageMutations();
  const { width } = useWindowDimensions();
  const onRenamePage = (newTitle: string) => {
    editPageTitle.mutate({ newTitle, pageId: page.id });
  };

  const abilities = React.useContext(AbilityContext);
  const isActive = page.id === activePageId;
  const isManagerOrGuideOwner = abilities.can(
    GUIDELINES_PERMISSIONS.CREATE,
    new GuideAbility(guideOwnerId),
  );
  const areSidebarItemsCollapsed = useSidebarStore(
    s => s.areSidebarItemsCollapsed,
  );

  const toggleIsSidebarCollapsed = useSidebarStore(
    s => s.toggleIsSidebarCollaped,
  );

  React.useEffect(() => {
    if (areSidebarItemsCollapsed) {
      setIsAccordionOpen(false);
    } else {
      setIsAccordionOpen(true);
    }
  }, [areSidebarItemsCollapsed]);

  React.useEffect(() => {
    if (isActive) setIsAccordionOpen(true);
    else setIsAccordionOpen(false);
  }, [isActive]);

  const isPageDisabled = !(
    isManagerOrGuideOwner ||
    page.canEdit ||
    page.canView
  );

  const [isAccordionOpen, setIsAccordionOpen] = React.useState(isActive);

  const handleClick = () => {
    setIsAccordionOpen(!isAccordionOpen);

    if (width < size.laptop) {
      toggleIsSidebarCollapsed(true);
    }

    if (page.id !== activePageId) {
      if (isEditMode && !page.canEdit) {
        setIsEditMode(false);
      }

      setActiveInspectorSetting(InspectorSetting.PAGE_VIEW);
      setActivePageId(page.id);
      history.push({
        pathname: ROUTES.GUIDE_PAGE(activeGuideId!, page.id),
        hash: undefined,
      });
    }
  };

  const isTableOfContentsDisplayed = page.tableOfContents.isVisibleInGuide;

  return (
    <StyledListContainer $isDragging={isDragging}>
      {isEditMode ? (
        <EditableSidebarItem
          labelText={page.title}
          onChange={onRenamePage}
          isPage
          isActive={isActive}
          onClick={handleClick}
        >
          <div>
            {page.state === PageStates.DRAFT && (
              <Badge isSecondary>Draft</Badge>
            )}
          </div>
        </EditableSidebarItem>
      ) : (
        <StyledPageTitle
          data-testid="pageName"
          onClick={handleClick}
          $isActive={isActive}
          $isDisabled={isPageDisabled}
        >
          {isTableOfContentsDisplayed ? (
            <div>
              {isAccordionOpen ? <IconCaretDown /> : <IconCaretRight />}
            </div>
          ) : (
            <div style={{ minWidth: '24px' }} />
          )}
          <p>{page.title}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {page.state === PageStates.DRAFT && (
              <Badge isSecondary>Draft</Badge>
            )}
          </div>
        </StyledPageTitle>
      )}
      {isTableOfContentsDisplayed && (
        <TableOfContents pageId={page.id} isAccordionOpen={isAccordionOpen} />
      )}
    </StyledListContainer>
  );
};

const TableOfContents: React.FC<{
  pageId: string;
  isAccordionOpen: boolean;
}> = ({ pageId, isAccordionOpen }) => {
  return <SidebarTableOfContents pageId={pageId} isOpen={isAccordionOpen} />;
};

const SidebarChapterHeader: React.FC<{
  chapter: Chapter;
  isAccordionOpen: boolean;
  setIsAccordionOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ chapter, setIsAccordionOpen, isAccordionOpen }) => {
  const history = useHistory();
  const [isHovering, setIsHovering] = React.useState(false);
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const activeChapterId = useGuideStore(s => s.activeChapterId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { data: guide } = useActiveGuide();
  const { renameChapter } = useChapterMutations();

  const isActiveChapter = activeChapterId === chapter.id;
  const displayPageTemplateModal = usePageTemplateModal();

  const canEditChapterTitle = chapter.canEdit || guide?.canEdit;
  const canAddPageToChapter = chapter.canEdit || guide?.canEdit;

  const onAddPageTemplate = () => {
    setIsAccordionOpen(true);

    displayPageTemplateModal({
      chapterId: chapter.id,
      entityType: EntityEnum.GUIDE_PAGE,
    });
  };

  const onRenameChapter = (newChapterTitle: string) => {
    renameChapter.mutate({ chapterId: chapter?.id, newChapterTitle });
  };

  const redirectToFirstChapterPage = () => {
    setIsAccordionOpen(!isAccordionOpen);
    const firstViewablePage = chapter?.pages.find(page => page.canView);

    if (firstViewablePage && activePageId !== firstViewablePage?.id) {
      setIsAccordionOpen(true);

      history.push({
        pathname: ROUTES.GUIDE_PAGE(activeGuideId!, firstViewablePage?.id),
      });
    }
  };

  const renderChapterTitle = () => {
    if (isEditMode && canEditChapterTitle) {
      return (
        <EditableSidebarItem
          data-testid="chapterTitle"
          labelText={chapter?.title}
          onChange={onRenameChapter}
        />
      );
    }

    return (
      <StyledChapterItemTitle
        $isHovering={isHovering}
        onClick={redirectToFirstChapterPage}
      >
        {chapter?.title}
      </StyledChapterItemTitle>
    );
  };

  return (
    <StyledChapterItemHeader
      data-testid="chapterContainer"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      $isActiveChapter={isActiveChapter}
    >
      <>
        {!!chapter?.pages.length && (
          <ExpandIcon
            handleClick={setIsAccordionOpen}
            isOpen={isAccordionOpen}
          />
        )}
        {renderChapterTitle()}
        <div
          style={{
            margin: 0,
            padding: 0,
            position: 'relative',
            display: isHovering || isActiveChapter ? 'flex' : 'none',
          }}
        >
          {isEditMode && canAddPageToChapter && (
            <SideBarChapterDropdown chapter={chapter} />
          )}
          {isEditMode && canAddPageToChapter && (
            <Button
              data-testid="createPageButton"
              aria-label="Add page"
              isSmall
              variant="clean"
              icon={<IconAdd />}
              onClick={onAddPageTemplate}
            />
          )}
        </div>
      </>
    </StyledChapterItemHeader>
  );
};

const SideBarChapterDropdown: React.FC<{
  chapter: Chapter;
}> = ({ chapter }) => {
  const openModal = useModalStore.use.openModal();
  const { translate } = useTranslate();
  const abilities = React.useContext(AbilityContext);
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const { data: guide } = useActiveGuide();
  const { data: currentUser } = useCurrentUserIdentity();
  const { isBasic } = useModuleTier();
  const { deleteChapter, renameChapter } = useChapterMutations();
  const isChapterOwner = chapter?.ownerId === currentUser?.id;

  const canDeleteChapter =
    (guide?.ownerId &&
      abilities.can(
        GUIDELINES_PERMISSIONS.CREATE,
        new GuideAbility(guide?.ownerId),
      )) ||
    isChapterOwner;
  const isManager = abilities.can(
    GUIDELINES_PERMISSIONS.MANAGE,
    PERMISSION_ENTITIES.GUIDE,
  );

  const canShareChapter =
    guide?.ownerId === currentUser?.id || isChapterOwner || isManager;

  const canEditChapterTitle = chapter?.canEdit || guide?.canEdit;

  const toggleEditChapterTitleModal = () =>
    openModal({
      modalType: ModalType.CREATE_MODAL,
      modalOnSubmit: onEditChapterTitle,
      modalProps: {
        initialValue: chapter?.title,
        headerText: translate('EDIT_CHAPTER_TITLE_MODAL.HEADER_TEXT'),
        buttonLabel: translate('EDIT_MODAL.BUTTON_SAVE'),
        labelText: translate('EDIT_CHAPTER_TITLE_MODAL.LABEL_TEXT'),
        placeholderText: translate('EDIT_CHAPTER_TITLE_MODAL.PLACEHOLDER_TEXT'),
        maxLength: CHAPTER_NAME_MAX_LENGTH,
      },
    });

  const toggleDeleteChapterModal = () =>
    openModal({
      modalType: ModalType.DELETE_MODAL,
      modalOnSubmit: onDeleteChapter,
      modalProps: {
        headerText: translate('DELETE_CHAPTER_MODAL.HEADER_TEXT'),
        contentText: translate('DELETE_CHAPTER_MODAL.CONTENT_TEXT', {
          title: chapter?.title,
        }),
        checkbox1Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_1_TEXT'),
        checkbox2Text: translate('DELETE_GUIDE_MODAL.CHECKBOX_2_TEXT'),
        buttonText: translate('DELETE_CHAPTER_MODAL.BUTTON_TEXT'),
      },
    });

  const toggleAccessModal = () =>
    openModal({
      modalType: ModalType.ACCESS_MODAL,
      modalOnSubmit: () => null,
      modalProps: {
        entityId: chapter?.id,
        activeGuideId,
        headerText: translate('ACCESS_MODAL.HEADER_TEXT', {
          title: chapter?.title,
        }),
      },
    });

  const onDeleteChapter = () => {
    deleteChapter.mutate({ chapterId: chapter?.id });
  };

  const onEditChapterTitle = (newChapterTitle: string) => {
    renameChapter.mutate({ chapterId: chapter?.id, newChapterTitle });
  };

  return (
    <Dropdown
      position="bottom-right"
      trigger={({ isOpen, ...triggerProps }) => {
        return (
          <Button
            isPressed={isOpen}
            data-testid="chapterDropdown"
            variant="clean"
            isSmall
            aria-label="Chapter dropdown"
            icon={<IconMore />}
            {...triggerProps}
          />
        );
      }}
    >
      {canEditChapterTitle && (
        <Dropdown.Item
          onClick={() => toggleEditChapterTitleModal()}
          icon={<IconEdit />}
          data-testid="editChapterButton"
        >
          <Translate id="CHAPTER_DROPDOWN.EDIT_TITLE_OPTION" />
        </Dropdown.Item>
      )}
      {canShareChapter && !isBasic && (
        <Dropdown.Item
          icon={<IconAddPerson />}
          onClick={() => toggleAccessModal()}
          data-testid="editChapterAccessButton"
        >
          <Translate id="GUIDE_KEBAB_MENU.GUIDE_ACCESS_ITEM" />
        </Dropdown.Item>
      )}
      {canDeleteChapter && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item
            data-testid="deleteChapterButton"
            onClick={() => toggleDeleteChapterModal()}
            icon={<IconDelete />}
          >
            <Translate id="CHAPTER_DROPDOWN.DELETE_CHAPTER_OPTION" />
          </Dropdown.Item>
        </>
      )}
    </Dropdown>
  );
};

const ExpandIcon = ({
  isOpen,
  handleClick,
}: {
  isOpen: boolean;
  handleClick?: (isAccordionOpen: boolean) => void;
}) => {
  return (
    <div onClick={() => handleClick && handleClick(isOpen ? false : true)}>
      {isOpen ? (
        <IconCaretDown style={{ flexShrink: 0 }} />
      ) : (
        <IconCaretRight style={{ flexShrink: 0 }} />
      )}
    </div>
  );
};

export const StyledListContainer = styled.div<{
  $isDragging?: boolean;
}>`
  padding-left: ${token.spacing4};
  min-height: 40px;
  position: relative;
  border-radius: ${token.radiusMedium};
  box-shadow: ${props => props.$isDragging && token.elevationStatic};
`;

export const StyledChapterItem = styled.div<{
  $isDragging?: boolean;
}>`
  box-shadow: ${props => (props.$isDragging ? token.elevationStatic : `none`)};
  box-sizing: content-box !important;
  padding: 0 ${token.spacing3};
  margin-top: ${token.spacing4};
  background-color: ${({ theme }) =>
    theme.getValueOrDefault('sideBar.backgroundColor.hexCode')};
`;

interface StyledChapterProps {
  theme: Themeset;
  $isActiveChapter: boolean;
}

export const StyledChapterItemHeader = styled.div<StyledChapterProps>(
  ({ theme, $isActiveChapter }) => {
    const { getValueOrDefault } = theme;

    return `
    min-height: 40px;
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    font-weight: 400;
    border-radius: ${token.radiusMedium};
    padding-right: ${token.spacing3};

    &:hover {
      background-color: ${getValueOrDefault(
        'sideBar.chapter.colors.background.hover.hexCode',
      )};
      svg {
        color: ${getAlphaColor(
          0.8,
          getValueOrDefault('sideBar.chapter.colors.text.hover.hexCode'),
        )};
      }
    }

    svg {
      color:     ${
        $isActiveChapter
          ? getAlphaColor(
              0.8,
              getValueOrDefault('sideBar.chapter.colors.text.hover.hexCode'),
            )
          : getAlphaColor(
              0.6,
              getValueOrDefault('sideBar.chapter.colors.text.normal.hexCode'),
            )
      };
    }
  `;
  },
);
